// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { DashboardShortcutSection } from './types';

// ** Shared
import { DashboardBodyText, DashboardPanel } from './shared';

// ** Components
import Link from '@/components/common/Link';

type DashboardShortcutPanelProps = {
	section: DashboardShortcutSection;
	sx?: SxProps<Theme>;
};

/**
 * Mobile-only nav button for a shortcut section.
 * Rendered in its own grid row in Dashboard — completely separate from the
 * desktop DashboardPanel so there is no risk of double-rendering.
 */
export const DashboardShortcutButton = ({ section }: { section: DashboardShortcutSection }) => (
	<Box
		component={section.cta ? Link : 'div'}
		{...(section.cta ? { href: section.cta.href } : {})}
		aria-label={section.title}
		sx={{
			display: 'block',
			width: '100%',
			height: '64px',
			borderRadius: '8px',
			backgroundColor: 'common.white',
			textDecoration: 'none',
			cursor: section.cta ? 'pointer' : 'default',
			transition: 'opacity 160ms ease',
			'&:hover': { opacity: section.cta ? 0.82 : 1 },
			'&:focusVisible': {
				outline: (theme: Theme) => `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
				outlineOffset: '2px'
			}
		}}
	>
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '100%',
				height: '100%'
			}}
		>
			<Typography variant="panel-heading" component="span" sx={{ m: 0, color: 'common.black', textAlign: 'center' }}>
				{section.title}
			</Typography>
		</Box>
	</Box>
);

/**
 * Desktop panel for a shortcut section.
 * Only rendered when md+ is active (display:none on mobile).
 */
const DashboardShortcutPanel = ({ section, sx }: DashboardShortcutPanelProps) => (
	<DashboardPanel
		title={section.title}
		cta={section.cta}
		compactOnDesktop={!section.body}
		sx={[{ display: { xs: 'none', md: 'flex' } }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
	>
		{section.body ? <DashboardBodyText>{section.body}</DashboardBodyText> : null}
	</DashboardPanel>
);

export default DashboardShortcutPanel;
